import {
  LUTS_SEARCH_COLLECTION,
  MOTION_GRAPHICS_COLLECTION,
  MUSIC_COLLECTION,
  SFX_COLLECTION,
  STOCK_VIDEO_COLLECTION,
} from '@music-vine/setlist';

/**
 * Resolves any search `type` query param value to the current versioned
 * Typesense collection name.
 *
 * Accepts URL slugs (`stock-video`, `motion-graphics`), generic aliases
 * (`music`, `sfx`, `mg`, `sv`), legacy bare names
 * (`track`, `tracks`, `motiongraphics`, `videos`), and versioned names (`tracks.v3`,
 * `sfx.v3`, `motiongraphics.v2`, `videos.v1`).
 *
 * Returns `null` if the value cannot be mapped to a known collection.
 */
export const resolveSearchTypeToCollection = (type: string): string | null => {
  // LUTs: routed to the synthetic marker so the search page branches into
  // motion graphics with `is_lut:true` and picks the LUTs label/tab. Checked
  // first because the marker itself starts with `motiongraphics.`.
  if (type === 'luts' || type === LUTS_SEARCH_COLLECTION) {
    return LUTS_SEARCH_COLLECTION;
  }

  // Music: music, track, tracks, tracks.*
  if (
    type === 'music' ||
    type === 'track' ||
    type === 'tracks' ||
    type.startsWith('tracks.')
  ) {
    return MUSIC_COLLECTION;
  }

  // SFX: sfx, sfx.*
  if (type === 'sfx' || type.startsWith('sfx.')) {
    return SFX_COLLECTION;
  }

  // Motion Graphics: mg, mg.*, motiongraphics, motiongraphics.*
  if (
    type === 'mg' ||
    type === 'motion-graphics' ||
    type === 'motiongraphics' ||
    type.startsWith('mg.') ||
    type.startsWith('motiongraphics.')
  ) {
    return MOTION_GRAPHICS_COLLECTION;
  }

  // Stock Video: sv, sv.*, videos, videos.*
  if (
    type === 'sv' ||
    type === 'stock-video' ||
    type === 'videos' ||
    type.startsWith('sv.') ||
    type.startsWith('videos.')
  ) {
    return STOCK_VIDEO_COLLECTION;
  }

  return null;
};

const aliasMap: Record<string, string> = {
  [MUSIC_COLLECTION]: 'music',
  [SFX_COLLECTION]: 'sfx',
  [MOTION_GRAPHICS_COLLECTION]: 'mg',
  [STOCK_VIDEO_COLLECTION]: 'sv',
  [LUTS_SEARCH_COLLECTION]: 'luts',
};

/**
 * Returns the canonical URL alias for a versioned Typesense collection name.
 *
 * `tracks.v3` → `'music'`, `sfx.v3` → `'sfx'`, `motiongraphics.v2` → `'mg'`, `videos.v1` → `'sv'`
 */
export const getSearchTypeAlias = (collectionName: string): string =>
  aliasMap[collectionName] ?? collectionName;
